import {Task, TaskEvent} from "@core/model";
import {createContext, useContext, useEffect, useState} from "react";
import {View} from "react-big-calendar";
import {Calendar as CalendarComp} from "./Calendar";
import {convertTasks} from "./util";
import {PopoverPosition} from "@mui/material";

type CalendarContextType = {
  view: View;
  tasks: Task[];
  range: {from: number; to: number} | undefined;
  setRange: React.Dispatch<
    React.SetStateAction<{from: number; to: number} | undefined>
  >;
  date?: Date;
  setDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  selected?: Task;
  setSelected: React.Dispatch<React.SetStateAction<Task | undefined>>;
  taskEvents: TaskEvent[];
  setTaskEvents: React.Dispatch<React.SetStateAction<TaskEvent[]>>;
  filter: {
    excluded: string[];
    position: undefined | PopoverPosition;
  };
  setFilter: React.Dispatch<
    React.SetStateAction<{
      excluded: string[];
      position: undefined | PopoverPosition;
    }>
  >;
};

const CalendarContext = createContext<CalendarContextType | undefined>(
  undefined,
);

const useCalendarContext = (): CalendarContextType => {
  const context = useContext(CalendarContext);

  if (!context) {
    throw new Error(
      "useCalendarContext must be used within a CalendarProvider",
    );
  }

  return context;
};

const Calendar: React.FC<{
  view: View;
  taskId?: string;
  onView: (view: View) => void;
}> = ({view, taskId, onView}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [range, setRange] = useState<{from: number; to: number}>();
  const [date, setDate] = useState<Date>();
  const [selected, setSelected] = useState<Task>();
  const [taskEvents, setTaskEvents] = useState<TaskEvent[]>([]);
  const [filter, setFilter] = useState<{
    excluded: string[];
    position: undefined | PopoverPosition;
  }>({excluded: [], position: undefined});

  // the filter keeps the excluded ids, so it survives the reload of the tasks (e.g. month change)
  useEffect(() => {
    const filtered = tasks.filter(t => {
      const ids = [t.assetId, t.assigneeId, ...(t.tags ?? [])].filter(
        (id): id is string => !!id,
      );
      // task without asset, user and tags should always be included
      return ids.length === 0 || ids.some(id => !filter.excluded.includes(id));
    });
    setTaskEvents(convertTasks(filtered));
  }, [tasks, filter.excluded]);

  return (
    <CalendarContext.Provider
      value={{
        view,
        tasks,
        setTasks,
        range,
        setRange,
        date,
        setDate,
        selected,
        setSelected,
        taskEvents,
        setTaskEvents,
        filter,
        setFilter,
      }}>
      <CalendarComp view={view} taskId={taskId} onView={onView} />
    </CalendarContext.Provider>
  );
};

export {Calendar, useCalendarContext};
